/**
 * I promemoria attivi: quelli che devono ancora venire.
 *
 * ## Cosa mostra, e cosa no
 *
 * Solo i programmi **non ancora finiti** — lo decide `upcomingReminders` nel dominio, non questa
 * schermata. «Non ancora finito» e non «non ancora cominciato»: quello in onda adesso è esattamente
 * ciò che chi apre questa pagina cerca, e filtrarlo via lo farebbe sparire nel momento in cui serve.
 *
 * ## Una riga anche senza il titolo
 *
 * Il programma può non esserci: il palinsesto in memoria è una finestra, e un promemoria messo
 * dall'hero su un evento di giovedì non ne ha nessuno. In quel caso la riga dice il **canale** dove
 * stava il titolo, e sotto solo il quando: un posto vuoto, o un «programma sconosciuto», direbbe che
 * qualcosa si è rotto proprio quando invece è tutto salvato.
 *
 * ## Una riga porta a guardare, la stellina toglie
 *
 * Due azioni diverse su bersagli diversi, e non due tasti uguali accanto: toccare la riga è il gesto
 * ovvio e porta al canale; togliere è distruttivo e sta in un angolo, dove non si finisce per
 * sbaglio mentre si scorre.
 */

import type { ReactNode } from 'react';
import { eventWhen, type ReminderEntry } from '@player/domain';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import { eventLine } from '../screens/HomeScreen.js';

export interface PhoneRemindersScreenProps {
  readonly entries: ReadonlyArray<ReminderEntry>;
  readonly channelNameOf: (channelId: string) => string;
  /** L'istante del server: decide «stasera» e «domani», e non va dedotto qui. */
  readonly nowMs: number;
  readonly onWatch: (channelId: string) => void;
  readonly onRemove: (channelId: string, startMs: number) => void;
}

export function PhoneRemindersScreen({
  entries,
  channelNameOf,
  nowMs,
  onWatch,
  onRemove,
}: PhoneRemindersScreenProps): ReactNode {
  const translator = useTranslator();

  return (
    <div
      data-phone-reminders="1"
      style={{ height: '100%', overflowY: 'auto', background: colors.background, color: colors.text }}
    >
      <h1
        style={{
          margin: 0,
          padding: '14px 20px',
          font: `800 min(7vw, 28px)/1.2 ${FONT_STACK}`,
          letterSpacing: '-.02em',
        }}
      >
        {translator.t('menu.reminders')}
      </h1>

      {entries.length === 0 ? (
        /*
          Si dice **dove si mettono**, non solo che non ce ne sono.

          «Nessun promemoria» da solo lascia chi guarda senza la cosa che gli serve: il gesto per
          averne uno. È la stessa scelta della pagina dei preferiti.
        */
        <p style={{ padding: '18px 20px', margin: 0, font: `500 15px/1.5 ${FONT_STACK}`, color: colors.textMuted }}>
          {translator.t('reminders.empty')}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: '0 20px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {entries.map((voce) => {
            const channel = channelNameOf(voce.channelId);
            /*
              Il quando lo compone `eventLine`, la stessa dell'hero.

              «Stasera alle 20:45» è una frase intera per ognuna delle sette lingue, e un promemoria
              di giovedì senza il nome del giorno sarebbe un orario che non dice niente. Riscriverla
              qui vorrebbe dire due composizioni da tenere d'accordo.

              Il canale si ripete nella seconda riga **solo** quando la prima porta il titolo:
              altrimenti sarebbe scritto due volte di fila.
            */
            const quando = eventLine(
              translator,
              eventWhen(voce.startMs, nowMs, false),
              voce.program === null ? null : channel,
            );
            return (
            <li key={voce.key} style={{ display: 'flex', alignItems: 'stretch', gap: 10 }}>
              <button
                type="button"
                data-reminder={voce.key}
                onClick={() => onWatch(voce.channelId)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 56,
                  textAlign: 'left',
                  padding: '10px 14px',
                  borderRadius: 12,
                  border: `1px solid ${colors.borderFaint}`,
                  background: colors.panel,
                  color: colors.text,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <span style={{ font: `600 15px/1.3 ${FONT_STACK}`, overflowWrap: 'anywhere' }}>
                  {voce.program === null ? channel : voce.program.title}
                </span>
                <span style={{ font: `500 13px ${FONT_STACK}`, color: colors.textFaint }}>
                  {quando}
                </span>
              </button>

              <button
                type="button"
                data-remove={voce.key}
                onClick={() => onRemove(voce.channelId, voce.startMs)}
                /*
                  Il nome dell'azione sta in `aria-label`: l'icona da sola non dice a un lettore di
                  schermo che questo tasto **toglie**, e qui l'errore non si annulla.
                */
                aria-label={translator.t('guide.action.reminderSet')}
                style={{
                  flex: 'none',
                  width: 56,
                  borderRadius: 12,
                  border: `1px solid ${colors.border}`,
                  background: 'transparent',
                  color: colors.accent,
                  font: `700 18px ${FONT_STACK}`,
                  cursor: 'pointer',
                }}
              >
                ★
              </button>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
